<template>
  <SpectreToastGroup :toasts="queue" />
  <SpectreNavigation
    :key="`${isImpersonating}`"
    :links="navigationLinks ?? []"
    product="spectre_id"
    :user="navUser"
    :environment="appEnv"
    :shadowbar="shadowbar"
    @request-access="handleRequestAccess"
  >
    <NavigationBarSearch v-if="canSearch" />
  </SpectreNavigation>
  <slot v-bind="$attrs" />
  <GlobalSearchPalette v-if="canSearch" />
</template>

<script setup lang="ts">
import { computed } from 'vue';
import {
  SpectreNavigation,
  SpectreToastGroup,
  useToasts,
  type NavigationLinkCollection,
  type ShadowbarData,
} from '@agentsoftware/spectre-ui';
import { router } from '@inertiajs/vue3';
import GlobalSearchPalette from '@/Components/Modals/GlobalSearchPaletteModal.vue';
import NavigationBarSearch from '@/Layouts/NavigationBarSearch.vue';

import { whenever } from '@vueuse/core';
import type { User } from '@/types';

const props = defineProps<{
  auth: { user: User | null };
  environment: string;
  flash: { toasts: AgentSoftware.SpectreToasts.Data.ToastData[] };
  navigationLinks?: NavigationLinkCollection[];
  shadowbarData?: ShadowbarData | null;
}>();

const { queue, enqueue } = useToasts();

whenever(() => props.flash.toasts, (newValue) => {
  newValue?.forEach((toast) => {
    enqueue(toast.label, toast.description, toast.type);
  });
}, { immediate: true });

const appEnv = computed(() => {
  switch (props.environment) {
    case 'local':
      return 'local';
    case 'uat':
      return 'uat';
    case 'demo':
      return 'demo';
    default:
      return 'production';
  }
});

const canSearch = computed(() => props.auth.user?.can?.search ?? false);
const isImpersonating = computed(() => props.auth.user?.impersonating ?? false);

const navUser = computed(() => ({
  name: props.auth.user?.name ?? '',
  roles: props.auth.user?.roles ?? [],
}));

const shadowbar = computed(() => props.shadowbarData ?? undefined);

const handleRequestAccess = (product: string) => {
  router.post(route('request-access', { productKey: product }));
};
</script>
